import { tinycolor, SVG, $, _ } from "js/vendor";
import React from "reactn";
import { ds } from "js/core/models/dataService";
import log from "js/core/logger";
import * as geom from "js/core/utilities/geom";
import imageFilters from "js/core/utilities/imageFilters";
import { AssetType, ASSET_FILETYPE, ASSET_BREAKPOINTS } from "common/constants";
import { BaseElement } from "js/elements/base/BaseElement";
import { isElectron } from "js/config";
import { timeoutRace, loadImage } from "js/core/utilities/promiseHelper";
import { ASSET_TIMEOUT } from "common/constants";
import { getCSSTransform } from "js/core/utilities/geom";
import { getElementTransition, SVGGroup, ELEMENT_TRANSITION_DURATION } from "../../core/utilities/svgHelpers";
import { renderAdjustmentFilter, renderColorFilter } from "../../core/utilities/svgFilters";

class Picture extends BaseElement {
    get assetId() {
        return this.model[this.bindTo];
    }

    get bindTo() {
        return this.options.bindTo || "content_value";
    }

    get _canSelect() {
        return false;
    }

    get _doubleClickToSelect() {
        return false;
    }

    get passThroughSelection() {
        return false;
    }

    get selectionPadding() {
        return 0;
    }

    get constrainAspectRatio() {
        return this.model.constrainAspectRatio;
    }

    get modelDefined() {
        return !["aoiLeft", "aoiRight", "aoiTop", "aoiBottom"].some(
            prop => this.model[prop] == null || isNaN(this.model[prop]) || !isFinite(this.model[prop]));
    }

    get backgroundColor() {
        if (this.model.backgroundColor && this.model.backgroundColor != "none") {
            return this.model.backgroundColor;
        } else if (this.asset && this.asset.get("hasSolidBackground")) {
            return tinycolor(this.asset.get("imageBackgroundColor")).toHexString();
        }
    }

    get hasAlpha() {
        if (this.asset) {
            return this.asset.get("hasAlpha");
        } else {
            return false;
        }
    }

    get hasSolidBackground() {
        if (this.asset) {
            return this.asset.get("hasSolidBackground");
        } else {
            return false;
        }
    }

    get isScaledBelowFill() {
        return this.getScale(this.calculatedProps.bounds.size) < this.calcFilledImage(this.calculatedProps.bounds.size).scale;
    }

    get imagesLoadPromise() {
        if (!window.isRenderer) {
            return Promise.reject(new Error("Supported only in renderer"));
        }

        // We don't have an image
        if (!this.assetUrl) {
            return Promise.resolve();
        }

        // The promise hasn't been initialized yet (probably we haven't rendered the image yet)
        if (!this.imageOnLoadPromises[this.assetUrl]) {
            return Promise.resolve();
        }

        return this.imageOnLoadPromises[this.assetUrl];
    }

    updateAsset(asset) {
        this.model[this.bindTo] = asset.id;
        this.model.content_type = asset.type;

        this.resetImageTransform();
    }

    resetImageTransform() {
        // reset the model so the default transform is applied when this new asset is loaded
        this.model.aoiLeft = this.model.aoiRight = this.model.aoiTop = this.model.aoiBottom = this.model.scale = null;
    }

    get initialSizeToLoadImageAt() {
        return window.isRenderer ? "original" : "xsmall";
    }

    async _load() {
        if (this.asset && this.hasImage && this.assetId == this.asset.id) return;

        if (this.assetId && this.assetId != "") {
            try {
                let asset = await ds.assets.getAssetById(this.assetId, "image");
                await asset.onReady();
                this.assetUrl = await asset.getURL(this.initialSizeToLoadImageAt);
                this.asset = asset;

                this.isOptimalSizeLoaded = false;
                this.hasImage = true;
            } catch (err) {
                log.error("Failed to load asset " + this.assetId + " : " + err);
                this.hasImage = false;
            }
        } else {
            this.asset = null;
            this.hasImage = false;
        }
    }

    refreshElement(transition) {
        this.canvas.refreshElement(this, transition);
    }

    get canRefreshElement() {
        return true;
    }

    get hflip() {
        if (this.modelDefined) {
            return this.model.flipHorizontal ? -1 : 1;
        }

        return 1;
    }

    _calcProps(props, options) {
        let { size } = props;
        if (!this.asset) return { size };

        let transform;
        if (this.modelDefined) {
            // image has been initialized, use model properties
            transform = {
                scale: this.adjustScale(this.getScale(size), new geom.Rect(0, 0, size)),
                hflip: this.hflip,
                offset: this.getOffset(size),
                center: this.getImageCenter()
            };
        } else {
            // no model is set for the image transform so calc the default transform and save
            if (options.optimalFitSize) {
                transform = this.calcFitImage(new geom.Rect(0, 0, options.optimalFitSize));
            } else if (options.fitAsset) {
                transform = this.calcFitImage(new geom.Rect(0, 0, options.fitSize || size));
            } else {
                transform = this.getDefaultImageTransform(size);
                transform.scale = Math.min(1.5, transform.scale);
            }
            this.updateModel(transform, size);
        }

        // if (options.fitAsset) {
        //     transform = this.calcFitImage(new geom.Rect(0, 0, options.fitSize || size));
        // }

        props.transformProps = {
            translateX: size.width / 2 - transform.hflip * (transform.center.x + transform.offset.x) * transform.scale,
            translateY: size.height / 2 - (transform.center.y + transform.offset.y) * transform.scale,
            scaleX: transform.scale * transform.hflip,
            scaleY: transform.scale
        };

        props.filter = this.model.filter;
        props.filterBrightness = this.model.filterBrightness;
        props.filterBlur = this.model.filterBlur;
        props.filterContrast = this.model.filterContrast;

        return { size };
    }

    loadOptimalSize() {
        this.optimalImageSize = this.calculateOptimalAssetSizeType(this.calculatedProps.paddedBounds);
        this.asset.getURL(this.optimalImageSize).then(url => {
            this.assetUrl = url;
            this.refreshElement(false);
        });
    }

    renderChildren(props, transition) {
        if (this.asset) {
            // If the optimal size image hasn't been loaded, load it and then refresh to render
            if (!this.isOptimalSizeLoaded && !window.isRenderer && !this.canvas.isExport) {
                // Setting isOptimalSizeLoaded to true immediately to avoid multiple loadOptimalSize() runs
                this.isOptimalSizeLoaded = true;
                this.canvas.layouter.runPostRender(() => {
                    if (transition) {
                        // We want to load the optimal size only after the transition is finished to avoid
                        // breaking the animation when the optimal sized image is loaded and rendered
                        _.delay(() => this.loadOptimalSize(), ELEMENT_TRANSITION_DURATION);
                    } else {
                        this.loadOptimalSize();
                    }
                });
            }

            return this.renderImage(props, transition);
        } else {
            return (
                <SVGGroup key={this.id}>
                    <g id="imageNode"></g>
                </SVGGroup>
            );
        }
    }

    renderImage(props, transition) {
        let { transformProps } = props;

        let imageFilterId;
        let colorFilter;
        if (props.filter && props.filter != "None") {
            if (this.colorFilters.includes(props.filter)) {
                colorFilter = renderColorFilter(this, props, this.canvas.getTheme());
                imageFilterId = `url(#${this.uniqueId}-color-filter)`;
            } else {
                imageFilterId = `url(#${props.filter})`;
            }
        }

        let adjustmentFilter;
        if (props.filterBrightness || props.filterBlur || props.filterContrast) {
            adjustmentFilter = renderAdjustmentFilter(this, props);
        }

        return (
            <SVGGroup overflowHidden key={this.id}>
                <g id="imageNode"
                    filter={adjustmentFilter ? `url(#${this.uniqueId}-adj-filter)` : null}
                    style={{ transform: getCSSTransform(transformProps), transition: getElementTransition(transition) }}
                >
                    {adjustmentFilter}
                    {colorFilter}
                    <image
                        href={this.assetUrl}
                        filter={imageFilterId}
                        width={this.getImageSize().width}
                        height={this.getImageSize().height}
                        style={{ imageOrientation: "from-image" }}
                        onLoad={this.getImageOnLoadPromiseResolver(this.assetUrl)}
                    />
                </g>
            </SVGGroup>
        );
    }

    getDefaultImageTransform(size) {
        if (this.asset.get("type") == AssetType.LOGO) {
            return this.calcFitImage(new geom.Rect(0, 0, size));
        } else {
            return this.calcFilledImage(new geom.Rect(0, 0, size));
        }
    }

    _prepareToShowElement() {
        if (this.asset && this.asset.get("fileType") === ASSET_FILETYPE.GIF && this.image) {
            this.image.attr("href", this.gifImage.src, SVG.xlink);
        }
    }

    _stopElement() {
        if (this.asset && this.asset.get("fileType") === ASSET_FILETYPE.GIF && this.image) {
            this.image.attr("href", "", SVG.xlink);
        }
    }

    loadImageFromAsset(asset, size) {
        if (!asset) {
            return Promise.reject(new Error("No asset"));
        }

        return asset.onReady()
            .then(asset => asset.getURL(size))
            .then(url => {
                this.asset = asset;
                return timeoutRace(ASSET_TIMEOUT, loadImage(url));
            });
    }

    loadImageFromURL(url, size) {
        const asset = this.asset;
        return timeoutRace(ASSET_TIMEOUT, loadImage(url)
            .catch(() => {
                log.info("Could not load image, attempting to cache bust asset url for " + asset.id);
                return asset.getURL(size, true).then(url => loadImage(url));
            }));
    }

    // get the optimal size type that should be loaded at the current canvas scale and element bounds
    calculateOptimalAssetSizeType(bounds = null) {
        if (this.asset.get("fileType") === ASSET_FILETYPE.GIF || this.asset.get("fileType") === ASSET_FILETYPE.SVG) {
            return "original";
        }

        let area = this.asset.get("h") * this.asset.get("w");
        // In the app, always use a deterministic size, maxing out at the 'xlarge' size
        if (isElectron) {
            let size = area > (1600 * 1600) ? "xlarge" : "original";
            return this.asset.has(size) ? size : "original";
        }

        let scale = (bounds || this.innerBounds)
            ? this.getScale(bounds || this.innerBounds)
            : (this.model.get("scale") || 1);
        let scaledArea = area * Math.pow(scale * this.canvas.getMaxScale(), 2);
        // * window.devicePixelRatio; mitch: 12/30 - i'm disabling highDPI support to see if it's a better performance/quality tradeoff
        if (scaledArea >= area) {
            return "xlarge";
        }
        for (var breakpoint of ASSET_BREAKPOINTS) {
            if (scaledArea > breakpoint.area) {
                return breakpoint.size;
            }
        }
    }

    // The native size of the asset before scaling
    getImageSize() {
        if (this.asset) {
            return new geom.Size(this.asset.get("w") + (this.styles.imagePadding || 0) * 2, this.asset.get("h") + (this.styles.imagePadding || 0) * 2);
        } else {
            // Not sure why this path would exist as the data doesn't make sense but it is a path that occurs when migrating the culturegrid template.
            return new geom.Size(0, 0);
        }
    }

    // The center point of the original asset
    getImageCenter() {
        return new geom.Point(this.getImageSize().width / 2, this.getImageSize().height / 2);
    }

    getScale(size) {
        let didFit = this.model.aoiRight <= this.getImageSize().width &&
            this.model.aoiLeft >= 0 &&
            this.model.aoiBottom <= this.getImageSize().height &&
            this.model.aoiTop >= 0;
        let heightScale = size.height / (this.model.aoiBottom - this.model.aoiTop);
        let widthScale = size.width / (this.model.aoiRight - this.model.aoiLeft);
        let finalScale = Math.max(heightScale, widthScale);
        if (didFit) {
            // ensure that changing layout will not cause a fitting image to stop fitting
            finalScale = Math.max(finalScale, geom.fillRect(this.getImageSize(), size));
        }
        let result = Math.max(this.minScale(size), Math.min(finalScale, this.maxScale(size)));
        if (isNaN(result)) return 1;
        return result;
    }

    adjustScale(scale, size) {
        let fillPct = scale / geom.fillRect(this.getImageSize(), size);
        // if (fillPct > 0.90) return Math.max(geom.fillRect(this.getImageSize(), bounds), scale);
        return scale;
    }

    shouldFitImage() {
        return false;
    }

    minScale(size) {
        // if (this.options.allowScaleBelowFill) {
        return geom.fitImageToRect(this.getImageSize(), size) * 0.5;
        // } else {
        //     return geom.fillRect(this.getImageSize(), size);
        // }
    }

    maxScale(size) {
        return Math.max(1.5, this.calcFilledImage(size).scale);
    }

    // How far is the image offset from the center of the element
    getOffset(size) {
        let center = this.getImageCenter();
        let x = (this.model.aoiLeft + this.model.aoiRight) / 2 - center.x;
        if (isNaN(x)) {
            x = 0;
        }
        let y = (this.model.aoiTop + this.model.aoiBottom) / 2 - center.y;
        if (isNaN(y)) {
            y = 0;
        }
        return this.clampOffset(new geom.Point(x, y), size);
    }

    // max offset - offset such that the edge of the image lines up with the edge of the element.
    maxOffset(size, scale) {
        if (scale === undefined) scale = this.getScale(size);
        scale = this.adjustScale(scale, size);

        let center = this.getImageCenter();

        if (this.shouldFitImage()) {
            return new geom.Point(0, 0);
        } else {
            return new geom.Point(
                Math.max(0, center.x - size.width / (2 * scale)),
                Math.max(0, center.y - size.height / (2 * scale)));
        }
    }

    minOffset(size, scale) {
        return this.maxOffset(size, scale).scale(-1);
    }

    // Take an offset point and clamp it between min and max
    clampOffset(offset, size, scale) {
        return offset.max(this.minOffset(size, scale)).min(this.maxOffset(size, scale));
    }

    calcFitImage(size, adjustment = 1) {
        let scale = geom.fitImageToRect(this.getImageSize(), size) * adjustment;
        return {
            scale: scale,
            hflip: this.hflip,
            offset: new geom.Point(0, 0),
            center: this.getImageCenter()
        };
    }

    calcFilledImage(size) {
        let scale = geom.fillRect(this.getImageSize(), size);
        return {
            scale: scale,
            hflip: this.hflip,
            offset: new geom.Point(0, 0),
            center: this.getImageCenter()
        };
    }

    updateModel(props, size) {
        let scale = "scale" in props ? props.scale : this.getScale(size);
        let offset = props.offset ? this.clampOffset(props.offset, size, scale) : this.getOffset(size);
        let center = this.getImageCenter();
        let flipHorizontal = "flipHorizontal" in props ? props.flipHorizontal : this.model.flipHorizontal;
        scale = Math.max(this.minScale(size), Math.min(this.maxScale(size), scale));

        let width = size.width / scale;
        let height = size.height / scale;
        let aoiLeft = center.x + offset.x - width / 2;
        let aoiRight = center.x + offset.x + width / 2;
        let aoiTop = center.y + offset.y - height / 2;
        let aoiBottom = center.y + offset.y + height / 2;
        Object.assign(this.model, { aoiLeft, aoiRight, aoiTop, aoiBottom, scale, flipHorizontal, version: 1 });
    }

    get colorFilters() {
        return ["theme", "accent1", "accent2", "accent3", "accent4", "accent5", "background_accent"];
    }

    get imageFilters() {
        return Object.keys(imageFilters);
    }
}

class Logo extends Picture {
    get isTransparent() {
        return this.asset.get("hasAlpha");
    }

    get backgroundColor() {
        // let color = super.backgroundColor;
        // if (color){
        //     return color;
        // } else {
        if (this.asset.get("hasAlpha")) {
            return "none";
        } else {
            return "white";
        }
        // }
    }

    get constrainAspectRatio() {
        return "fit";
    }

    minScale(size) {
        return this.calcFitImage(size, 0.5).scale;
    }

    maxScale(size) {
        return this.calcFitImage(size, 1).scale;
    }

    getDefaultImageTransform(size) {
        return this.calcFitImage(size, this.options.defaultScale || 0.9);
    }
}

class LogoXXX extends Picture {
    // get backgroundColor() {
    //     if (this.isTransparent) {
    //         return "none";
    //     } else {
    //         return "white";
    //     }
    // }

    get _doubleClickToSelect() {
        return true;
    }

    get _canSelect() {
        return false;
    }

    get isTransparent() {
        return this.asset.get("hasAlpha");
    }

    getScale(size) {
        return size.width / this.getImageSize().width;
    }

    // temp hack until logos are trimmed at import time because we need the inital size and optimal size to be the same
    // or the trimmed size changes
    get initialSizeToLoadImageAt() {
        return "original";
    }

    calculateOptimalAssetSizeType() {
        return "original";
    }

    //     getImageSize() {
    //         return this.trimmedSize;
    //     }

    //     loadImageFromURL(url, size) {
    //         return super.loadImageFromURL(url, size).then(img => {
    //             return new Promise(resolve => {
    //                 let image = new Image();
    //                 image.onload = () => {
    //                     this.trimmedSize = new geom.Size(image.naturalWidth, image.naturalHeight);
    //                     resolve(image);
    //                 };
    //                 image.src = trimWhiteEdges(img);
    //             });
    //         }
    //         );
    //     }
}

export { Picture, Logo };
